import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Check, X, ChevronDown, Plus, Shield } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";

/**
 * SmartPairSuggestion — appears at the top of the cart drawer.
 *
 * Suggests ONE medically-vetted treatment that pairs well with the most
 * recently added cart item, when it's clinically safe to do in the same
 * visit. The matrix below is sourced from 2025 RN injector best-practice
 * guidance (Botox+Filler different zones, Microneedling+PRP same session,
 * etc.) and intentionally OMITS combos that should be staged 1-2 weeks
 * apart (e.g., Microneedling + Filler in the same area).
 *
 * UX:
 * - Champagne/cream banner above the cart items
 * - "Yes, take me there" → routes to the suggested treatment page, closes drawer
 * - "No thanks" → dismissed for the session
 * - Auto-hides after 10s if no interaction
 * - One suggestion at a time, never re-shown the same session
 */

const PAIRING_MATRIX = {
  botox: {
    suggestKey: "filler",
    path: "/dermal-fillers",
    titleEn: "Pair with Dermal Filler",
    titleFr: "Combinez avec un Filler",
    reasonEn:
      "Botox smooths dynamic wrinkles where muscles move — filler restores the volume Botox can't. Done together in the same visit when areas don't overlap (cheeks, chin, lips).",
    reasonFr:
      "Le Botox lisse les rides dynamiques — le filler restaure le volume que le Botox ne peut pas. Combinés en une seule visite quand les zones ne se chevauchent pas.",
    chipEn: "Same-visit safe · Different zones",
    chipFr: "Sans danger · Zones différentes",
  },
  filler: {
    suggestKey: "botox",
    path: "/botox-ottawa",
    titleEn: "Add Botox to your visit",
    titleFr: "Ajoutez du Botox à votre visite",
    reasonEn:
      "Filler restores volume, but dynamic wrinkles (forehead, 11's, crow's feet) need Botox. Combining in one visit treats both static and movement-related aging at once.",
    reasonFr:
      "Le filler restaure le volume, mais les rides dynamiques nécessitent du Botox. Combinez en une visite pour traiter le vieillissement statique et dynamique.",
    chipEn: "Same-visit safe · Different zones",
    chipFr: "Sans danger · Zones différentes",
  },
  "lip-filler": {
    suggestKey: "botox",
    path: "/botox-ottawa",
    titleEn: "Balance lip volume with upper-face Botox",
    titleFr: "Équilibrez avec du Botox du haut du visage",
    reasonEn:
      "A beautiful lip enhancement looks even more refined when crow's feet, 11's, and forehead lines are softened. Common pairing — completely safe same visit.",
    reasonFr:
      "Une augmentation des lèvres est encore plus raffinée avec du Botox sur le haut du visage. Combinaison courante, sans danger en même temps.",
    chipEn: "Same-visit safe · Most popular pairing",
    chipFr: "Sans danger · Combinaison populaire",
  },
  lip: {
    // alias for "lip-filler"
    suggestKey: "botox",
    path: "/botox-ottawa",
    titleEn: "Balance lip volume with upper-face Botox",
    titleFr: "Équilibrez avec du Botox du haut du visage",
    reasonEn:
      "A beautiful lip enhancement looks even more refined when crow's feet, 11's, and forehead lines are softened. Common pairing — completely safe same visit.",
    reasonFr:
      "Une augmentation des lèvres est encore plus raffinée avec du Botox sur le haut du visage.",
    chipEn: "Same-visit safe · Most popular pairing",
    chipFr: "Sans danger · Combinaison populaire",
  },
  microneedling: {
    suggestKey: "prp",
    path: "/prp-ottawa",
    titleEn: "Upgrade to the Vampire Facial (add PRP)",
    titleFr: "Ajoutez du PRP (Vampire Facial)",
    reasonEn:
      "PRP applied DURING your microneedling session uses your own growth factors to boost collagen 2–3x. Layered safely in one visit — this is the original Vampire Facial.",
    reasonFr:
      "Le PRP appliqué pendant votre microneedling utilise vos propres facteurs de croissance pour doubler ou tripler la production de collagène.",
    chipEn: "Same-session layering · Clinically proven",
    chipFr: "En même temps · Cliniquement prouvé",
  },
  prp: {
    suggestKey: "microneedling",
    path: "/medical-microneedling",
    titleEn: "Pair PRP with Medical Microneedling",
    titleFr: "Combinez le PRP avec le Microneedling Médical",
    reasonEn:
      "PRP needs micro-channels in the skin to absorb deeply. Microneedling creates them — together they're 10x more effective than PRP alone.",
    reasonFr:
      "Le PRP a besoin de micro-canaux dans la peau pour bien pénétrer. Le microneedling les crée — ensemble, 10x plus efficaces.",
    chipEn: "Same-session pairing · Optimal absorption",
    chipFr: "En même temps · Absorption optimale",
  },
  sculptra: {
    suggestKey: "botox",
    path: "/botox-ottawa",
    titleEn: "Smooth dynamic wrinkles while Sculptra rebuilds",
    titleFr: "Lissez les rides pendant que Sculptra reconstruit",
    reasonEn:
      "Sculptra slowly stimulates collagen over months. Botox in a different zone (forehead, 11's) smooths movement wrinkles immediately while Sculptra works underneath.",
    reasonFr:
      "Sculptra stimule lentement le collagène. Le Botox dans une autre zone lisse immédiatement les rides de mouvement.",
    chipEn: "Same-visit safe · Different zones",
    chipFr: "Sans danger · Zones différentes",
  },
};

// Derive a treatment type from a cart item — accepts the explicit `type`
// field when present, falls back to keyword matching the name + category
// so we still get a useful suggestion for items added before the type field existed.
const detectType = (item) => {
  if (!item) return null;
  if (item.type) {
    const t = String(item.type).toLowerCase();
    if (PAIRING_MATRIX[t]) return t;
    if (t === "lip-filler") return "lip";
    if (t === "neuromodulator") return "botox";
  }
  const blob = `${item.name || ""} ${item.category || ""} ${item.id || ""}`.toLowerCase();
  if (/microneedl|skinpen|rejuvapen|morpheus/.test(blob)) return "microneedling";
  if (/\bprp\b|vampire facial|platelet/.test(blob)) return "prp";
  if (/\blip\b/.test(blob)) return "lip";
  if (/sculptra/.test(blob)) return "sculptra";
  if (/filler|juvederm|restylane|revanesse|teosyal|skin booster|nefertiti/.test(blob)) return "filler";
  if (/botox|dysport|xeomin|neuromodulator|forehead|11's|crow|glabella|masseter/.test(blob)) return "botox";
  return null;
};

const DISMISSED_KEY = "klc:pair_dismissed_v1";
const getDismissed = () => {
  try {
    return JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || "[]");
  } catch {
    return [];
  }
};
const dismiss = (key) => {
  try {
    const arr = new Set(getDismissed());
    arr.add(key);
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...arr]));
  } catch {
    /* ignore quota / private mode */
  }
};

const SmartPairSuggestion = ({ onAcceptClose }) => {
  const { cartItems, setIsCartOpen } = useCart();
  const { language } = useLanguage();
  const isFr = language === "fr";
  const [hidden, setHidden] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Pick the suggestion based on the MOST RECENTLY added cart item that has
  // a known type and a non-dismissed, non-already-in-cart pairing.
  const suggestion = useMemo(() => {
    if (!cartItems || cartItems.length === 0) return null;
    const cartTypes = new Set(cartItems.map(detectType).filter(Boolean));
    const dismissed = new Set(getDismissed());
    // Iterate newest → oldest so the latest add wins
    for (let i = cartItems.length - 1; i >= 0; i--) {
      const t = detectType(cartItems[i]);
      const rule = PAIRING_MATRIX[t];
      if (!rule) continue;
      // Skip if the suggested pairing (or its alias) is already in cart
      const suggestedT = rule.suggestKey === "lip-filler" ? "lip" : rule.suggestKey;
      if (cartTypes.has(rule.suggestKey) || cartTypes.has(suggestedT)) continue;
      const key = `${t}->${rule.suggestKey}`;
      if (dismissed.has(key)) continue;
      return { ...rule, key, sourceType: t };
    }
    return null;
  }, [cartItems]);

  // Auto-dismiss after 10s of inactivity per suggestion shown
  useEffect(() => {
    if (!suggestion || hidden) return;
    const tid = setTimeout(() => setHidden(true), 10000);
    return () => clearTimeout(tid);
  }, [suggestion, hidden]);

  // Reset hidden state when the suggestion key changes (new item added)
  useEffect(() => {
    setHidden(false);
    setExpanded(false);
  }, [suggestion?.key]);

  if (!suggestion || hidden) return null;

  const handleNo = () => {
    dismiss(suggestion.key);
    setHidden(true);
  };

  const handleYes = () => {
    dismiss(suggestion.key);
    if (typeof onAcceptClose === "function") onAcceptClose();
    if (typeof setIsCartOpen === "function") setIsCartOpen(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        key={suggestion.key}
        initial={{ opacity: 0, y: -8, height: 0 }}
        animate={{ opacity: 1, y: 0, height: "auto" }}
        exit={{ opacity: 0, y: -8, height: 0 }}
        transition={{ duration: 0.28 }}
        className="mb-4 overflow-hidden"
        data-testid="smart-pair-suggestion"
      >
        <div
          className="rounded-xl p-4 border border-amber-200/80 shadow-sm"
          style={{
            background:
              "linear-gradient(135deg, #faf4e6 0%, #fbf6ea 40%, #f7eed6 100%)",
          }}
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white/80 border border-amber-200 flex items-center justify-center shadow-sm">
              <Sparkles className="w-4 h-4 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider">
                  <Shield className="w-2.5 h-2.5" />
                  {isFr ? "S'associe bien" : "Pairs well"}
                </span>
              </div>
              <h4
                className="font-semibold text-stone-900 text-[15px] leading-snug"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="smart-pair-title"
              >
                {isFr ? suggestion.titleFr : suggestion.titleEn}
              </h4>
              <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-800/80 hover:text-amber-900 transition"
                data-testid="smart-pair-why"
              >
                {isFr ? "Pourquoi ça fonctionne" : "Why this pairs well"}
                <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {expanded && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="text-[12px] text-stone-700 leading-relaxed mt-2"
                  >
                    {isFr ? suggestion.reasonFr : suggestion.reasonEn}
                    <span className="block mt-1.5 text-[10.5px] text-amber-800 font-medium">
                      {isFr ? suggestion.chipFr : suggestion.chipEn}
                    </span>
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
            <button
              type="button"
              onClick={handleNo}
              className="flex-shrink-0 text-stone-400 hover:text-stone-700 transition p-1 -mt-1 -mr-1"
              aria-label={isFr ? "Fermer" : "Dismiss"}
              data-testid="smart-pair-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-2 mt-3.5">
            <button
              type="button"
              onClick={handleNo}
              className="flex-1 px-3 py-2 rounded-lg text-sm font-medium text-stone-700 bg-white/70 hover:bg-white border border-stone-200 hover:border-stone-300 transition"
              data-testid="smart-pair-no"
            >
              {isFr ? "Non merci" : "No thanks"}
            </button>
            <Link
              to={suggestion.path}
              onClick={handleYes}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white bg-amber-700 hover:bg-amber-800 shadow-sm transition"
              data-testid="smart-pair-yes"
            >
              <Plus className="w-4 h-4" />
              {isFr ? "Voir" : "Show me"}
            </Link>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SmartPairSuggestion;
